import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, UserRound, Stethoscope, StickyNote, History, Loader2, Send, Building2, MessageSquare, Plus } from "lucide-react";
import { api } from "../api";
import { useAdminAuth } from "../AdminAuthContext";
import {
    Panel, Loading, LoadError, StatusBadge, PriorityBadge, ReferralBadge, SourceBadge,
    STATUS_LABELS, CASE_STATUSES, PRIORITIES, SOURCE_LABELS, ENQUIRY_SOURCES,
    REFERRAL_STATUSES, COMMUNICATION_TYPES, COMM_TYPE_LABELS, fmtDate, fmtDateTime,
} from "../ui";

const selectCls =
    "rounded-lg border border-[#1D322E] bg-[#0E1A17] px-3 py-2 text-xs text-[#F7F5EF] outline-none focus:border-[#10B981] transition-colors disabled:opacity-50";

const Pipeline = ({ status }) => {
    const activeIdx = CASE_STATUSES.indexOf(status);
    return (
        <div className="flex flex-wrap gap-1.5" data-testid="status-stepper">
            {CASE_STATUSES.map((s, i) => (
                <span
                    key={s}
                    data-testid={`status-stepper-item-${s}`}
                    title={STATUS_LABELS[s]}
                    className={`rounded-md px-2 py-1 text-[10px] font-semibold tracking-wide border transition-colors ${
                        i < activeIdx
                            ? "bg-emerald-900/60 text-emerald-300 border-emerald-700/60"
                            : i === activeIdx
                            ? "bg-emerald-500 text-[#0B1513] font-bold shadow-md shadow-emerald-500/20 border-emerald-500"
                            : "bg-slate-800/60 text-slate-500 border-slate-700/50"
                    }`}
                >
                    {STATUS_LABELS[s]}
                </span>
            ))}
        </div>
    );
};

const DetailRow = ({ label, value }) => (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-[#1A2E2A] last:border-0">
        <dt className="text-xs uppercase tracking-wider text-[#64716D] shrink-0">{label}</dt>
        <dd className="text-sm text-[#F7F5EF] text-right break-words">{value || "—"}</dd>
    </div>
);

export const AdminCaseDetail = () => {
    const { caseId } = useParams();
    const { user } = useAdminAuth();
    const isAdmin = user?.role === "ADMIN";

    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const [coordinators, setCoordinators] = useState([]);
    const [note, setNote] = useState("");
    const [noteSaving, setNoteSaving] = useState(false);
    const [noteError, setNoteError] = useState("");
    const [updating, setUpdating] = useState(false);

    // Referrals
    const [hospitals, setHospitals] = useState([]);
    const [refForm, setRefForm] = useState({ hospital_id: "", status: "REFERRAL_PLANNED", referral_date: "", notes: "" });
    const [refSaving, setRefSaving] = useState(false);
    const [refError, setRefError] = useState("");
    const [refBusy, setRefBusy] = useState("");

    // Communication log
    const [commForm, setCommForm] = useState({ type: "WHATSAPP", note: "" });
    const [commSaving, setCommSaving] = useState(false);
    const [commError, setCommError] = useState("");

    const load = () =>
        api
            .get(`/admin/cases/${caseId}`)
            .then((res) => {
                setData(res.data);
                setError("");
            })
            .catch(() => setError("Could not load this case. It may not exist or you may not have access."));

    useEffect(() => {
        setData(null);
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [caseId]);

    useEffect(() => {
        if (!isAdmin) return;
        api.get("/admin/coordinators")
            .then((res) => setCoordinators(res.data.items))
            .catch(() => {});
    }, [isAdmin]);

    useEffect(() => {
        api.get("/admin/hospitals?limit=100&active=true")
            .then((res) => setHospitals(res.data.items))
            .catch(() => {});
    }, []);

    const patch = async (payload) => {
        setUpdating(true);
        try {
            await api.patch(`/admin/cases/${caseId}`, payload);
            await load();
        } catch (err) {
            setError(err.response?.data?.detail || "Update failed. Please try again.");
        } finally {
            setUpdating(false);
        }
    };

    const submitNote = async (e) => {
        e.preventDefault();
        if (!note.trim()) return;
        setNoteSaving(true);
        setNoteError("");
        try {
            await api.post(`/admin/cases/${caseId}/notes`, { note: note.trim() });
            setNote("");
            await load();
        } catch (err) {
            setNoteError(err.response?.data?.detail || "Could not add the note. Please try again.");
        } finally {
            setNoteSaving(false);
        }
    };

    const submitReferral = async (e) => {
        e.preventDefault();
        if (!refForm.hospital_id) {
            setRefError("Please choose a hospital.");
            return;
        }
        setRefSaving(true);
        setRefError("");
        try {
            await api.post(`/admin/cases/${caseId}/referrals`, {
                ...refForm,
                referral_date: refForm.referral_date || null,
                notes: refForm.notes || null,
            });
            setRefForm({ hospital_id: "", status: "REFERRAL_PLANNED", referral_date: "", notes: "" });
            await load();
        } catch (err) {
            const detail = err.response?.data?.detail;
            setRefError(typeof detail === "string" ? detail : "Could not create the referral. Please try again.");
        } finally {
            setRefSaving(false);
        }
    };

    const updateReferralStatus = async (referralId, status) => {
        setRefBusy(referralId);
        try {
            await api.patch(`/admin/referrals/${referralId}`, { status });
            await load();
        } catch (err) {
            const detail = err.response?.data?.detail;
            setRefError(typeof detail === "string" ? detail : "Could not update the referral.");
        } finally {
            setRefBusy("");
        }
    };

    const submitCommunication = async (e) => {
        e.preventDefault();
        if (!commForm.note.trim()) return;
        setCommSaving(true);
        setCommError("");
        try {
            await api.post(`/admin/cases/${caseId}/communications`, { ...commForm, note: commForm.note.trim() });
            setCommForm({ type: commForm.type, note: "" });
            await load();
        } catch (err) {
            const detail = err.response?.data?.detail;
            setCommError(typeof detail === "string" ? detail : "Could not log the communication. Please try again.");
        } finally {
            setCommSaving(false);
        }
    };

    if (error && !data) return <LoadError message={error} />;
    if (!data) return <Loading />;

    const { case: c, patient, notes, activities } = data;

    return (
        <div data-testid="admin-case-detail">
            <Link
                to="/admin/cases"
                data-testid="case-back-link"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#A3B8B0] hover:text-[#F7F5EF] transition-colors"
            >
                <ArrowLeft className="w-3.5 h-3.5" /> All cases
            </Link>

            {/* Header */}
            <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight text-[#F7F5EF] font-mono" data-testid="case-detail-title">
                        {c.case_id}
                    </h1>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                        <StatusBadge status={c.status} />
                        <PriorityBadge priority={c.priority} />
                        <span className="text-xs text-[#64716D]">
                            Opened {fmtDate(c.created_at)} · source {c.enquiry_source || "—"}
                        </span>
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    {isAdmin ? (
                        <select
                            data-testid="coordinator-assign-select"
                            value={c.assigned_coordinator?.id || ""}
                            onChange={(e) => patch({ assigned_coordinator_id: e.target.value })}
                            disabled={updating}
                            className={selectCls}
                            aria-label="Assigned coordinator"
                        >
                            <option value="">Unassigned</option>
                            {coordinators.map((co) => (
                                <option key={co.id} value={co.id}>
                                    {co.name} ({co.role})
                                </option>
                            ))}
                        </select>
                    ) : (
                        <span className="text-xs text-[#A3B8B0]">
                            Coordinator: {c.assigned_coordinator?.name || "Unassigned"}
                        </span>
                    )}
                    <select
                        data-testid="case-priority-select"
                        value={c.priority}
                        onChange={(e) => patch({ priority: e.target.value })}
                        disabled={updating}
                        className={selectCls}
                        aria-label="Priority"
                    >
                        {PRIORITIES.map((p) => (
                            <option key={p} value={p}>{p}</option>
                        ))}
                    </select>
                    <select
                        data-testid="case-status-select"
                        value={c.status}
                        onChange={(e) => patch({ status: e.target.value })}
                        disabled={updating}
                        className={selectCls}
                        aria-label="Status"
                    >
                        {CASE_STATUSES.map((s) => (
                            <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                        ))}
                    </select>
                    <select
                        data-testid="case-source-select"
                        value={c.source || "WEBSITE"}
                        onChange={(e) => patch({ source: e.target.value })}
                        disabled={updating}
                        className={selectCls}
                        aria-label="Enquiry source"
                    >
                        {ENQUIRY_SOURCES.map((s) => (
                            <option key={s} value={s}>{SOURCE_LABELS[s]}</option>
                        ))}
                    </select>
                    {updating && <Loader2 className="w-4 h-4 animate-spin text-emerald-400" />}
                </div>
            </div>

            {error && <div className="mt-4"><LoadError message={error} /></div>}

            <Panel className="mt-5 px-5 py-4">
                <Pipeline status={c.status} />
            </Panel>

            <div className="mt-6 grid lg:grid-cols-3 gap-6">
                {/* Left column — patient + medical */}
                <div className="lg:col-span-2 space-y-6">
                    <Panel className="p-5" data-testid="case-patient-card">
                        <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-emerald-400">
                            <UserRound className="w-4 h-4" /> Patient dossier
                        </h2>
                        <dl className="mt-3">
                            <DetailRow label="Name" value={patient?.name || c.patient_name} />
                            <DetailRow label="Email" value={patient?.email || c.email} />
                            <DetailRow label="Phone / WhatsApp" value={c.whatsapp || patient?.phone} />
                            <DetailRow label="Country" value={c.country || patient?.country} />
                            <DetailRow label="City" value={patient?.city} />
                            <DetailRow label="Date of birth" value={patient?.date_of_birth} />
                            <DetailRow label="Preferred language" value={c.preferred_language} />
                            <DetailRow label="Consent" value={c.consent_status} />
                        </dl>
                    </Panel>

                    <Panel className="p-5" data-testid="case-medical-card">
                        <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-emerald-400">
                            <Stethoscope className="w-4 h-4" /> Medical summary
                        </h2>
                        <dl className="mt-3">
                            <DetailRow label="Condition / concern" value={c.medical_condition} />
                            <DetailRow label="Specialty" value={c.specialty} />
                            <DetailRow label="Treatment interest" value={c.treatment_interest} />
                            <DetailRow label="Preferred destination" value={c.preferred_destination} />
                            <DetailRow label="Documents" value={c.documents?.length ? `${c.documents.length} file(s) — metadata only` : "None yet"} />
                        </dl>
                    </Panel>

                    <Panel className="p-5" data-testid="case-referrals-panel">
                        <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-emerald-400">
                            <Building2 className="w-4 h-4" /> Hospital / Referral
                        </h2>
                        <ul className="mt-4 space-y-3" data-testid="case-referrals-list">
                            {(data.referrals || []).length === 0 && (
                                <li className="text-sm text-[#64716D]">No hospital referral yet for this case.</li>
                            )}
                            {(data.referrals || []).map((r) => (
                                <li key={r.id} data-testid={`referral-item-${r.id}`} className="rounded-lg border border-[#1D322E] bg-[#0E1A17] px-4 py-3">
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                        <Link to={`/admin/hospitals/${r.hospital_id}`} className="text-sm font-semibold text-emerald-300 hover:text-emerald-200">
                                            {r.hospital_name}
                                        </Link>
                                        <div className="flex items-center gap-2">
                                            {refBusy === r.id ? (
                                                <Loader2 className="w-3.5 h-3.5 animate-spin text-emerald-400" />
                                            ) : (
                                                <select
                                                    data-testid={`referral-status-select-${r.id}`}
                                                    value={r.status}
                                                    onChange={(e) => updateReferralStatus(r.id, e.target.value)}
                                                    className={selectCls}
                                                    aria-label="Referral status"
                                                >
                                                    {REFERRAL_STATUSES.map((s) => (
                                                        <option key={s} value={s}>{s.replaceAll("_", " ")}</option>
                                                    ))}
                                                </select>
                                            )}
                                            <ReferralBadge status={r.status} />
                                        </div>
                                    </div>
                                    <p className="mt-1.5 text-xs text-[#64716D]">
                                        Referred {r.referral_date ? fmtDate(r.referral_date) : "— date not set"} · logged by {r.created_by_name} · {fmtDateTime(r.updated_at)}
                                    </p>
                                    {r.notes && <p className="mt-2 text-sm text-[#F7F5EF] leading-relaxed">{r.notes}</p>}
                                </li>
                            ))}
                        </ul>
                        <form onSubmit={submitReferral} className="mt-4 border-t border-[#1A2E2A] pt-4">
                            <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#A3B8B0]">
                                <Plus className="w-3.5 h-3.5" /> New referral
                            </p>
                            <div className="mt-3 grid sm:grid-cols-2 gap-3">
                                <select
                                    data-testid="referral-hospital-select"
                                    value={refForm.hospital_id}
                                    onChange={(e) => setRefForm((f) => ({ ...f, hospital_id: e.target.value }))}
                                    className={selectCls}
                                    aria-label="Hospital"
                                >
                                    <option value="">Select hospital…</option>
                                    {hospitals.map((h) => (
                                        <option key={h.id} value={h.id}>{h.display_name || h.legal_name} — {h.city}</option>
                                    ))}
                                </select>
                                <select
                                    data-testid="referral-new-status"
                                    value={refForm.status}
                                    onChange={(e) => setRefForm((f) => ({ ...f, status: e.target.value }))}
                                    className={selectCls}
                                    aria-label="Referral status"
                                >
                                    {REFERRAL_STATUSES.map((s) => (
                                        <option key={s} value={s}>{s.replaceAll("_", " ")}</option>
                                    ))}
                                </select>
                                <input
                                    data-testid="referral-new-date"
                                    type="date"
                                    value={refForm.referral_date}
                                    onChange={(e) => setRefForm((f) => ({ ...f, referral_date: e.target.value }))}
                                    className={selectCls}
                                    aria-label="Referral date"
                                />
                                <input
                                    data-testid="referral-new-notes"
                                    value={refForm.notes}
                                    onChange={(e) => setRefForm((f) => ({ ...f, notes: e.target.value }))}
                                    placeholder="Internal referral notes"
                                    className={selectCls}
                                />
                            </div>
                            {refError && (
                                <p role="alert" data-testid="referral-error" className="mt-3 rounded-lg border border-red-900/60 bg-red-950/40 px-3 py-2 text-xs text-red-300">
                                    {refError}
                                </p>
                            )}
                            <button
                                type="submit"
                                data-testid="referral-submit-button"
                                disabled={refSaving}
                                className="mt-3 inline-flex items-center gap-2 rounded-lg border border-emerald-800/70 bg-emerald-950/40 px-4 py-2 text-xs font-bold text-emerald-300 transition-all hover:bg-emerald-950/70 active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {refSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Building2 className="w-3.5 h-3.5" />}
                                Attach hospital referral
                            </button>
                        </form>
                    </Panel>

                    <Panel className="p-5" data-testid="case-notes-panel">
                        <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-emerald-400">
                            <StickyNote className="w-4 h-4" /> Internal notes
                        </h2>
                        <form onSubmit={submitNote} className="mt-4">
                            <textarea
                                data-testid="add-note-textarea"
                                rows={3}
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                placeholder="Add an internal note for the team (patients never see this)…"
                                className="w-full rounded-lg border border-[#1D322E] bg-[#0E1A17] px-4 py-3 text-sm text-[#F7F5EF] placeholder-[#64716D] outline-none focus:border-[#10B981] transition-colors resize-y"
                            />
                            {noteError && (
                                <p role="alert" data-testid="note-error" className="mt-2 text-xs font-semibold text-red-400">
                                    {noteError}
                                </p>
                            )}
                            <button
                                type="submit"
                                data-testid="submit-note-button"
                                disabled={noteSaving || !note.trim()}
                                className="mt-3 inline-flex items-center gap-2 rounded-lg bg-[#10B981] px-4 py-2 text-xs font-bold text-[#0B1513] transition-all hover:bg-emerald-400 active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {noteSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                                Add note
                            </button>
                        </form>
                        <ul className="mt-5 space-y-3" data-testid="case-notes-list">
                            {notes.length === 0 && (
                                <li className="text-sm text-[#64716D]">No notes yet — the team hasn't written anything.</li>
                            )}
                            {notes.map((n) => (
                                <li key={n.id || n.created_at} className="rounded-lg border border-[#1D322E] bg-[#0E1A17] px-4 py-3">
                                    <p className="text-sm text-[#F7F5EF] leading-relaxed whitespace-pre-wrap">{n.note}</p>
                                    <p className="mt-2 text-xs text-[#64716D]">
                                        {n.author_name} · {fmtDateTime(n.created_at)}
                                    </p>
                                </li>
                            ))}
                        </ul>
                    </Panel>

                    <Panel className="p-5" data-testid="case-communications-panel">
                        <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-emerald-400">
                            <MessageSquare className="w-4 h-4" /> Communication log
                        </h2>
                        <form onSubmit={submitCommunication} className="mt-4">
                            <div className="flex flex-wrap gap-3">
                                <select
                                    data-testid="communication-type-select"
                                    value={commForm.type}
                                    onChange={(e) => setCommForm((f) => ({ ...f, type: e.target.value }))}
                                    className={selectCls}
                                    aria-label="Communication type"
                                >
                                    {COMMUNICATION_TYPES.map((t) => (
                                        <option key={t} value={t}>{COMM_TYPE_LABELS[t]}</option>
                                    ))}
                                </select>
                                <input
                                    data-testid="communication-note-input"
                                    value={commForm.note}
                                    onChange={(e) => setCommForm((f) => ({ ...f, note: e.target.value }))}
                                    placeholder="Short note about this contact…"
                                    className="flex-1 min-w-[200px] rounded-lg border border-[#1D322E] bg-[#0E1A17] px-4 py-2 text-sm text-[#F7F5EF] placeholder-[#64716D] outline-none focus:border-[#10B981] transition-colors"
                                />
                                <button
                                    type="submit"
                                    data-testid="communication-submit-button"
                                    disabled={commSaving || !commForm.note.trim()}
                                    className="inline-flex items-center gap-2 rounded-lg bg-[#10B981] px-4 py-2 text-xs font-bold text-[#0B1513] transition-all hover:bg-emerald-400 active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {commSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                                    Log
                                </button>
                            </div>
                            {commError && (
                                <p role="alert" data-testid="communication-error" className="mt-2 text-xs font-semibold text-red-400">
                                    {commError}
                                </p>
                            )}
                        </form>
                        <ul className="mt-5 space-y-3" data-testid="case-communications-list">
                            {(data.communications || []).length === 0 && (
                                <li className="text-sm text-[#64716D]">No communications logged yet.</li>
                            )}
                            {(data.communications || []).map((c) => (
                                <li key={c.id || c.created_at} className="rounded-lg border border-[#1D322E] bg-[#0E1A17] px-4 py-3">
                                    <div className="flex items-center justify-between gap-3">
                                        <span className="text-xs font-bold uppercase tracking-wider text-emerald-300" data-testid={`communication-type-${c.type}`}>
                                            {COMM_TYPE_LABELS[c.type] || c.type}
                                        </span>
                                        <span className="text-xs text-[#64716D]">{fmtDateTime(c.created_at)}</span>
                                    </div>
                                    <p className="mt-1.5 text-sm text-[#F7F5EF] leading-relaxed">{c.note}</p>
                                    <p className="mt-1 text-xs text-[#64716D]">{c.staff_name}</p>
                                </li>
                            ))}
                        </ul>
                    </Panel>
                </div>

                {/* Right column — timeline */}
                <Panel className="p-5 h-fit" data-testid="case-activity-timeline">
                    <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-emerald-400">
                        <History className="w-4 h-4" /> Activity
                    </h2>
                    <ol className="mt-4 relative border-l border-[#1D322E] space-y-5 ml-2">
                        {activities.map((a, i) => (
                            <li key={`${a._id || i}`} className="ml-4">
                                <span className="absolute -left-[5px] mt-1.5 h-2.5 w-2.5 rounded-full bg-emerald-500/80" />
                                <p className="text-sm font-medium text-[#F7F5EF]">{a.event}</p>
                                <p className="text-xs text-[#64716D] mt-0.5">
                                    {a.user_name} · {fmtDateTime(a.created_at)}
                                </p>
                                {a.metadata && Object.keys(a.metadata).length > 0 && (
                                    <p className="mt-1 text-[11px] text-[#A3B8B0] break-words">
                                        {Object.entries(a.metadata)
                                            .filter(([, v]) => v !== null && v !== undefined && v !== "")
                                            .map(([k, v]) => `${k}: ${v}`)
                                            .join(" · ")}
                                    </p>
                                )}
                            </li>
                        ))}
                    </ol>
                </Panel>
            </div>
        </div>
    );
};
